import React from "react";
import { Client } from "../types";

interface ClientDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    client: Client | null;
    onApprove: (client: Client) => void;
    onReject: (client: Client) => void;
    onDelete: (client: Client) => void;
}

const ClientDetailModal: React.FC<ClientDetailModalProps> = ({
    isOpen,
    onClose,
    client,
    onApprove,
    onReject,
    onDelete,
}) => {
    if (!isOpen || !client) return null;

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("fr-FR", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md animate-[fadeIn_0.2s_ease-out]"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                <div
                    className="relative w-full max-w-3xl bg-gradient-to-b from-primary-light to-primary-dark rounded-3xl shadow-2xl shadow-black/50 pointer-events-auto animate-[slideUp_0.3s_ease-out] max-h-[90vh] flex flex-col overflow-hidden border border-white/10"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header with gradient */}
                    <div className="relative px-6 pt-6 pb-4 bg-gradient-to-r from-accent/10 via-transparent to-accent/10">
                        {/* Close button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-200"
                        >
                            <svg
                                className="w-5 h-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>

                        {/* Client avatar and info */}
                        <div className="flex items-center space-x-4">
                            <div className="relative">
                                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-accent to-accent-light flex items-center justify-center shadow-lg shadow-accent/25">
                                    <span className="text-white text-3xl font-bold">
                                        {client.username
                                            .charAt(0)
                                            .toUpperCase()}
                                    </span>
                                </div>
                                {/* Status indicator */}
                                <div
                                    className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-4 border-primary-light flex items-center justify-center ${
                                        client.is_approved
                                            ? "bg-green-500"
                                            : "bg-yellow-500"
                                    }`}
                                >
                                    {client.is_approved ? (
                                        <svg
                                            className="w-3 h-3 text-white"
                                            fill="currentColor"
                                            viewBox="0 0 20 20"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            className="w-3 h-3 text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={3}
                                                d="M12 8v4l3 3"
                                            />
                                        </svg>
                                    )}
                                </div>
                            </div>
                            <div className="flex-1">
                                <h2 className="text-2xl font-bold text-white mb-1">
                                    {client.username}
                                </h2>
                                <div className="flex items-center space-x-3">
                                    <span className="text-gray-500 text-sm">
                                        ID: {client.id}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
                                {/* Info cards grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Username card */}
                                    <div className="bg-primary-dark/50 rounded-xl p-4 border border-white/5">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                                                <svg
                                                    className="w-5 h-5 text-accent"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                    Nom d'utilisateur
                                                </p>
                                                <p className="text-white font-semibold">
                                                    {client.username}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* ID card */}
                                    <div className="bg-primary-dark/50 rounded-xl p-4 border border-white/5">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                                <svg
                                                    className="w-5 h-5 text-blue-400"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                    Identifiant
                                                </p>
                                                <p className="text-white font-semibold">
                                                    #{client.id}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Date card */}
                                    <div className="bg-primary-dark/50 rounded-xl p-4 border border-white/5">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                                                <svg
                                                    className="w-5 h-5 text-purple-400"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                    />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                    Date d'inscription
                                                </p>
                                                <p className="text-white font-semibold">
                                                    {formatDate(
                                                        client.created_at,
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Status card */}
                                    <div className="bg-primary-dark/50 rounded-xl p-4 border border-white/5">
                                        <div className="flex items-center space-x-3 mb-2">
                                            <div
                                                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                    client.is_approved
                                                        ? "bg-green-500/10"
                                                        : "bg-yellow-500/10"
                                                }`}
                                            >
                                                {client.is_approved ? (
                                                    <svg
                                                        className="w-5 h-5 text-green-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                ) : (
                                                    <svg
                                                        className="w-5 h-5 text-yellow-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                )}
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                    Statut
                                                </p>
                                                <p
                                                    className={`font-semibold ${client.is_approved ? "text-green-400" : "text-yellow-400"}`}
                                                >
                                                    {client.is_approved
                                                        ? "Approuve"
                                                        : "En attente"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Role info */}
                                <div className="bg-gradient-to-r from-accent/5 to-transparent rounded-xl p-4 border border-accent/20">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                                            <svg
                                                className="w-6 h-6 text-accent"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                                                />
                                            </svg>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase tracking-wide">
                                                Role
                                            </p>
                                            <p className="text-white font-semibold capitalize">
                                                {client.role}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                    </div>

                    {/* Footer actions */}
                    <div className="px-6 py-4 bg-primary-dark/50 border-t border-white/10">
                        {client.is_approved ? (
                            /* Actions pour les clients approuvés */
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <button
                                    onClick={() => {
                                        onClose();
                                        onDelete(client);
                                    }}
                                    className="flex items-center space-x-2 px-4 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-all duration-200"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                    <span className="text-sm font-medium">
                                        Supprimer
                                    </span>
                                </button>

                                <button
                                    onClick={onClose}
                                    className="px-5 py-2.5 text-sm font-medium text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-200"
                                >
                                    Fermer
                                </button>
                            </div>
                        ) : (
                            /* Actions pour les clients en attente - uniquement Rejeter et Approuver */
                            <div className="flex items-center justify-end gap-3 px-4">
                                <button
                                    onClick={() => {
                                        onClose();
                                        onReject(client);
                                    }}
                                    className="flex items-center space-x-2 px-5 py-2.5 text-sm font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-xl transition-all duration-200"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                    <span>Rejeter</span>
                                </button>
                                <button
                                    onClick={() => {
                                        onClose();
                                        onApprove(client);
                                    }}
                                    className="flex items-center space-x-2 px-5 py-2.5 text-sm font-semibold text-primary-dark bg-gradient-to-r from-green-500 to-green-400 hover:from-green-400 hover:to-green-300 rounded-xl shadow-lg shadow-green-500/25 hover:shadow-green-500/40 transition-all duration-200"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                    <span>Approuver</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Styles */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.98);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                @keyframes scaleIn {
                    from {
                        opacity: 0;
                        transform: scale(0.9);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1);
                    }
                }
            `}</style>
        </>
    );
};

export default ClientDetailModal;
